import React from 'react'

export interface GuidelineData {
  number: string
  title: {
    cs: string
    en: string
  }
  content: {
    cs: React.ReactNode
    en: React.ReactNode
  }
  policyLink?: string
}

export const guidelines: Record<string, GuidelineData> = {
  '1': {
    number: '1',
    title: {
      cs: 'Nepropagujte, nekoordinujte ani se nezapojujte do obtěžování.',
      en: 'Do not promote, coordinate, or engage in harassment.',
    },
    content: {
      cs: (
        <>
          Nepovolujeme žádný typ obtěžujícího chování, včetně sexuálního obtěžování, obcházení banů
          nebo blokování nebo koordinování připojení k serverům za účelem obtěžování členů serveru
          ("server raiding"). (Více informací najdete v našem{' '}
          <a href="https://discord.com/safety/bullying-harassment-threats-policy-explainer">
            Vysvětlení zásad týkajících se šikany, obtěžování a výhrůžek
          </a>
          .)
        </>
      ),
      en: (
        <>
          We do not allow any type of harassing behavior, including sexual harassment, ban or block
          evasion, or coordinating server joins for the purpose of harassing server members
          (sometimes called "server raiding"). (See our{' '}
          <a href="https://discord.com/safety/bullying-harassment-threats-policy-explainer">
            Bullying, Harassment, and Threats Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
  },
  '2': {
    number: '2',
    title: {
      cs: 'Nevyhrožujte ublížením jiné osoby nebo skupině lidí.',
      en: 'Do not threaten to harm another individual or group of people.',
    },
    content: {
      cs: (
        <>
          To zahrnuje přímé, nepřímé a sugestivní hrozby. (Více informací najdete v našem{' '}
          <a href="https://discord.com/safety/bullying-harassment-threats-policy-explainer">
            Vysvětlení zásad týkajících se šikany, obtěžování a výhrůžek
          </a>
          .)
        </>
      ),
      en: (
        <>
          This includes direct, indirect, and suggestive threats. (See our{' '}
          <a href="https://discord.com/safety/bullying-harassment-threats-policy-explainer">
            Bullying, Harassment, and Threats Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
  },
  '3': {
    number: '3',
    title: {
      cs: 'Nesdílejte ani nevyhrožujte sdílením osobních identifikovatelných informací (PII) jednotlivce bez souhlasu.',
      en: 'Do not share or threaten to share personally identifiable information (PII) of an individual without consent.',
    },
    content: {
      cs: (
        <>
          To zahrnuje poskytování služeb, které usnadňují doxxing, jako je nákup nebo prodej doxxů
          nebo kompilace doxxů pro ostatní. (Více informací najdete v našem{' '}
          <a href="https://discord.com/safety/doxxing-policy-explainer">
            Vysvětlení zásad doxxingu
          </a>
          .)
        </>
      ),
      en: (
        <>
          This includes providing services that facilitate doxxing, such as buying or selling doxxes
          or dox compilations for others. (See our{' '}
          <a href="https://discord.com/safety/doxxing-policy-explainer">Doxxing Policy Explainer</a>{' '}
          for more.)
        </>
      ),
    },
  },
  '4': {
    number: '4',
    title: {
      cs: 'Nepoužívejte nenávistné projevy ani se nezapojujte do jiného nenávistného chování.',
      en: 'Do not use hate speech or engage in other hateful conduct.',
    },
    content: {
      cs: (
        <>
          To zahrnuje používání nenávistných symbolů, obrazů a tvrzení, která popírají historii
          masových lidských zvěrstev.
          <p>
            Za <strong>nenávistné projevy</strong> považujeme jakoukoli formu vyjádření, která buď
            útočí na jiné lidi, nebo propaguje nenávist nebo násilí vůči nim na základě jejich
            chráněných charakteristik. (Více informací najdete v našem{' '}
            <a href="/discord/safety/hateful-conduct-policy-explainer">
              Vysvětlení zásad nenávistného chování
            </a>
            .)
          </p>
        </>
      ),
      en: (
        <>
          This includes using hateful symbols, imagery, and claims that deny the history of mass
          human atrocities.
          <p>
            We consider <strong>hate speech</strong> to be any form of expression that either
            attacks other people or promotes hatred or violence against them based on their
            protected characteristics. (See our{' '}
            <a href="/discord/safety/hateful-conduct-policy-explainer">
              Hateful Conduct Policy Explainer
            </a>{' '}
            for more.)
          </p>
        </>
      ),
    },
    policyLink: '/discord/safety/hateful-conduct-policy-explainer',
  },
  '5': {
    number: '5',
    title: {
      cs: 'Neorganizujte, nepropagujte ani nepodporujte násilný extremismus.',
      en: 'Do not organize, promote, or support violent extremism.',
    },
    content: {
      cs: (
        <>
          To také zahrnuje koordinaci násilných činů; oslavování nebo propagaci násilí nebo
          pachatelů násilných činů; a propagaci konspiračních teorií, které by mohly povzbudit nebo
          podnítit násilí proti ostatním. (Více informací najdete v našich vysvětleních zásad{' '}
          <a href="/discord/safety/violent-extremism-policy-explainer">Násilný extremismus</a> a{' '}
          <a href="https://discord.com/safety/violence-graphic-content-policy-explainer">
            Násilí a grafický obsah
          </a>
          .)
        </>
      ),
      en: (
        <>
          This also includes coordinating violent acts; celebrating or promoting violence or violent
          perpetrators; and promoting conspiracy theories that could encourage or incite violence
          against others. (See our{' '}
          <a href="/discord/safety/violent-extremism-policy-explainer">Violent Extremism</a> and{' '}
          <a href="https://discord.com/safety/violence-graphic-content-policy-explainer">
            Violence and Graphic Content Policy Explainers
          </a>{' '}
          for more.)
        </>
      ),
    },
    policyLink: '/discord/safety/violent-extremism-policy-explainer',
  },
  '6': {
    number: '6',
    title: {
      cs: 'Nevytvářejte, nezveřejňujte, nevyžadujte, nesdílejte ani se nepokoušejte distribuovat obsah, který zobrazuje, propaguje nebo se pokouší normalizovat sexuální zneužívání dětí.',
      en: 'Do not create, post, solicit, share, or attempt to distribute content that depicts, promotes, or attempts to normalize child sexual abuse.',
    },
    content: {
      cs: (
        <>
          Také nezveřejňujte obsah ani se nezapojujte do chování, které jakýmkoli způsobem
          sexualizuje děti, včetně skutečných, smyšlených či antropomorfizovaných postav. To
          zahrnuje skutečná i manipulovaná média, animace, kresby, text a digitální tvorbu včetně
          AI, příkazů a promptů k vytváření takového obsahu.
          <p>
            Materiál sexuálního zneužívání dětí (CSAM) a grooming hlásíme{' '}
            <a href="https://www.missingkids.org/">
              Národnímu centru pro pohřešované a zneužívané děti
            </a>
            , které může následně spolupracovat s místními orgány činnými v trestním řízení. (Více
            informací najdete v našem{' '}
            <a href="https://discord.com/safety/child-safety-policy-explainer">
              Vysvětlení zásad bezpečnosti teenagerů a dětí
            </a>
            .)
          </p>
        </>
      ),
      en: (
        <>
          Also, do not post content or engage in conduct that in any way sexualizes children,
          including real, fictional, or anthropomorphized characters. This includes real as well as
          manipulated media, animation, drawings, text, and digital creation, including AI,
          commands, and prompts for producing such content.
          <p>
            We report child sexual abuse material (CSAM) and grooming to the{' '}
            <a href="https://www.missingkids.org/">
              National Center for Missing & Exploited Children
            </a>
            , which may subsequently work with local law enforcement. (See our{' '}
            <a href="https://discord.com/safety/child-safety-policy-explainer">
              Teen and Child Safety Policy Explainer
            </a>{' '}
            for more.)
          </p>
        </>
      ),
    },
  },
  '7': {
    number: '7',
    title: {
      cs: 'Pokud je vám méně než 18 let, nezapojujte se do sexuálního chování ani do jakéhokoli chování, které ohrožuje vaši online nebo fyzickou bezpečnost.',
      en: 'If you are under the age of 18, do not engage in sexual conduct or any conduct that puts your online or physical safety at risk.',
    },
    content: {
      cs: (
        <>
          To zahrnuje i konsensuální sexuální interakce mezi stejně starými teenagery, stejně jako
          povzbuzování nebo koordinaci chování ohrožujícího vás či ostatní, například vigilantismus.
          <p>
            Chceme, aby se teenageři mohli na Discordu co nejvíce svobodně vyjadřovat, ale vzhledem
            k rizikům spojeným s online seznamováním odstraníme prostory, které podporují nebo
            usnadňují seznamování mezi teenagery. (Více informací najdete v našem{' '}
            <a href="https://discord.com/safety/child-safety-policy-explainer">
              Vysvětlení zásad bezpečnosti teenagerů a dětí
            </a>
            .)
          </p>
        </>
      ),
      en: (
        <>
          This includes consensual sexual interactions between same-age teens, as well as
          encouragement or coordination of behavior that puts you or others at risk, such as
          vigilantism.
          <p>
            We want teens to be able to express themselves freely on Discord as much as possible,
            but given the risks associated with online dating, we will remove spaces that encourage
            or facilitate dating between teens. (See our{' '}
            <a href="https://discord.com/safety/child-safety-policy-explainer">
              Teen and Child Safety Policy Explainer
            </a>{' '}
            for more.)
          </p>
        </>
      ),
    },
  },
  '8': {
    number: '8',
    title: {
      cs: 'Nevyžadujte sexuální obsah od nikoho mladšího 18 let, nesdílejte ani nevyměňujte s ním takový obsah a nezapojujte se s ním do sexuálního chování.',
      en: 'Do not solicit sexual content from anyone under the age of 18, share or exchange such content with them, or engage in sexual conduct with them.',
    },
    content: {
      cs: (
        <>
          Materiál sexuálního zneužívání dětí (CSAM) a grooming hlásíme{' '}
          <a href="https://www.missingkids.org/">
            Národnímu centru pro pohřešované a zneužívané děti
          </a>
          , které může následně spolupracovat s místními orgány činnými v trestním řízení. (Více
          informací najdete v našem{' '}
          <a href="https://discord.com/safety/child-safety-policy-explainer">
            Vysvětlení zásad bezpečnosti teenagerů a dětí
          </a>
          .)
        </>
      ),
      en: (
        <>
          We report child sexual abuse material (CSAM) and grooming to the{' '}
          <a href="https://www.missingkids.org/">
            National Center for Missing & Exploited Children
          </a>
          , which may subsequently work with local law enforcement. (See our{' '}
          <a href="https://discord.com/safety/child-safety-policy-explainer">
            Teen and Child Safety Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
  },
  '9': {
    number: '9',
    title: {
      cs: 'Nezpřístupňujte sexuálně explicitní obsah nikomu mladšímu 18 let.',
      en: 'Do not expose anyone under the age of 18 to sexually explicit content.',
    },
    content: {
      cs: (
        <>
          Pro zapojení do obsahu pro dospělé na Discordu vám musí být alespoň 18 let.
          <p>
            Majitelé serverů musí věkově omezit kanály se sexuálně explicitním obsahem pro dospělé i
            jiným obsahem pro dospělé sdíleným výhradně pro sexuální uspokojení.
          </p>
          <p>
            Uživatelé nesmí zveřejňovat sexuálně explicitní obsah v žádném prostoru, který nemůže
            být věkově omezen, včetně avatarů, vlastních statusů nebo biografií, bannerů serverů,
            ikon serverů, pozvánek, emoji a samolepek. (Více informací najdete v našem{' '}
            <a href="https://discord.com/safety/sexual-content-policy-explainer">
              Vysvětlení zásad sexuálního obsahu
            </a>
            .)
          </p>
        </>
      ),
      en: (
        <>
          You must be 18 or older to engage with adult content on Discord.
          <p>
            Server owners must age-restrict channels containing sexually explicit adult content or
            other adult content shared solely for sexual gratification.
          </p>
          <p>
            Users may not post sexually explicit content in any space that cannot be age-restricted,
            including in avatars, custom statuses or bios, server banners, server icons, invite
            splashes, emoji, and stickers. (See our{' '}
            <a href="https://discord.com/safety/sexual-content-policy-explainer">
              Sexual Content Policy Explainer
            </a>{' '}
            for more.)
          </p>
        </>
      ),
    },
  },
  '10': {
    number: '10',
    title: {
      cs: 'Nesdílejte, nedistribuujte ani nevytvářejte sexuálně explicitní nebo sexuálně sugestivní obsah zobrazující jiné dospělé bez jejich výslovného souhlasu.',
      en: 'Do not share, distribute, or create sexually explicit or sexually suggestive content depicting other adults without their explicit consent.',
    },
    content: {
      cs: (
        <>
          To zahrnuje nekonsensuální distribuci intimních médií, která byla vytvořena buď se
          souhlasem jednotlivce, nebo bez něj. (Více informací najdete v našem{' '}
          <a href="https://discord.com/safety/non-consensual-intimate-media-policy-explainer">
            Vysvětlení zásad nekonsensuálních intimních médií pro dospělé
          </a>
          .)
        </>
      ),
      en: (
        <>
          This includes the non-consensual distribution of intimate media that was created either
          with or without an individual's consent. (See our{' '}
          <a href="https://discord.com/safety/non-consensual-intimate-media-policy-explainer">
            Non-Consensual Intimate Media Policy for Adults Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
  },
  '11': {
    number: '11',
    title: {
      cs: 'Nesdílejte obsah, který oslavuje, propaguje nebo normalizuje sebevraždu nebo jiné činy fyzického sebepoškozování.',
      en: 'Do not share content that glorifies, promotes, or normalizes suicide or other acts of physical self-harm.',
    },
    content: {
      cs: (
        <>
          To zahrnuje obsah, který povzbuzuje ostatní k řezání, pálení nebo hladovění, stejně jako
          obsah, který normalizuje poruchy příjmu potravy, jako je anorexie a bulimie. Činy nebo
          hrozby sebepoškození používané jako forma emocionální manipulace nebo nátlaku jsou také
          zakázány. (Více informací najdete v našem{' '}
          <a href="/discord/safety/suicide-self-harm-policy-explainer">
            Vysvětlení zásad sebevraždy a sebepoškozování
          </a>
          .)
        </>
      ),
      en: (
        <>
          This includes content that encourages others to cut, burn, or starve themselves, as well
          as content that normalizes eating disorders, such as anorexia and bulimia. Self-harm acts
          or threats used as a form of emotional manipulation or coercion are also prohibited. (See
          our{' '}
          <a href="/discord/safety/suicide-self-harm-policy-explainer">
            Suicide and Self-Harm Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
    policyLink: '/discord/safety/suicide-self-harm-policy-explainer',
  },
  '12': {
    number: '12',
    title: {
      cs: 'Nesdílejte materiály zobrazující krveprolití, násilí nebo týrání zvířat, zejména s úmyslem obtěžovat nebo šokovat ostatní.',
      en: 'Do not share material depicting gore, violence, or animal cruelty, especially with the intention to harass or shock others.',
    },
    content: {
      cs: (
        <>
          (Více informací najdete v našem{' '}
          <a href="https://discord.com/safety/violence-graphic-content-policy-explainer">
            Vysvětlení zásad násilí a grafického obsahu
          </a>
          .)
        </>
      ),
      en: (
        <>
          (See our{' '}
          <a href="https://discord.com/safety/violence-graphic-content-policy-explainer">
            Violence and Graphic Content Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
  },
  '13': {
    number: '13',
    title: {
      cs: 'Neposílejte ostatním nevyžádané hromadné zprávy (nebo spam).',
      en: 'Do not send unsolicited bulk messages (or spam) to others.',
    },
    content: {
      cs: (
        <>
          Také tuto činnost neusnadňujte, například prodejem spambotů, nástrojů pro "raid" serverů,
          nástrojů pro vytváření účtů, generátorů tokenů, služeb řešení CAPTCHA nebo jiných
          spamových nástrojů. (Více informací najdete v našem{' '}
          <a href="/discord/safety/platform-manipulation-policy-explainer">
            Vysvětlení zásad manipulace s platformou
          </a>
          .)
        </>
      ),
      en: (
        <>
          Also, do not facilitate this activity, such as by selling spambots, server "raid" tools,
          account-creation tools, token generators, CAPTCHA-solving services, or other spam tools.
          (See our{' '}
          <a href="/discord/safety/platform-manipulation-policy-explainer">
            Platform Manipulation Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
    policyLink: '/discord/safety/platform-manipulation-policy-explainer',
  },
  '14': {
    number: '14',
    title: {
      cs: 'Nepoužívejte self-boty nebo user-boty.',
      en: 'Do not use self-bots or user-bots.',
    },
    content: {
      cs: (
        <>
          Každý účet musí být spojen s člověkem, nikoli s botem. (Více informací najdete v našem{' '}
          <a href="/discord/safety/platform-manipulation-policy-explainer">
            Vysvětlení zásad manipulace s platformou
          </a>
          .)
        </>
      ),
      en: (
        <>
          Each account must be associated with a human, not a bot. (See our{' '}
          <a href="/discord/safety/platform-manipulation-policy-explainer">
            Platform Manipulation Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
    policyLink: '/discord/safety/platform-manipulation-policy-explainer',
  },
  '15': {
    number: '15',
    title: {
      cs: 'Nezapojujte se do naší služby neautentickým způsobem.',
      en: 'Do not engage with our service in an inauthentic way.',
    },
    content: {
      cs: (
        <>
          To zahrnuje umělé navyšování členství na serveru, manipulaci s metrikami zapojení a prodej
          služeb umělého zapojení. (Více informací najdete v našem{' '}
          <a href="/discord/safety/platform-manipulation-policy-explainer">
            Vysvětlení zásad manipulace s platformou
          </a>
          .)
        </>
      ),
      en: (
        <>
          This includes artificially inflating server membership, manipulating engagement metrics,
          and selling artificial engagement services. (See our{' '}
          <a href="/discord/safety/platform-manipulation-policy-explainer">
            Platform Manipulation Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
    policyLink: '/discord/safety/platform-manipulation-policy-explainer',
  },
  '16': {
    number: '16',
    title: {
      cs: 'Neprodávejte ani nenakupujte aktiva Discord',
      en: 'Do not sell or purchase Discord assets',
    },
    content: {
      cs: (
        <>
          včetně účtů, uživatelských jmen, serverů, oprávnění serverů nebo vlastních odkazů na
          pozvánky na server. (Více informací najdete v našem{' '}
          <a href="/discord/safety/platform-manipulation-policy-explainer">
            Vysvětlení zásad manipulace s platformou
          </a>
          .)
        </>
      ),
      en: (
        <>
          including accounts, usernames, servers, server permissions, or custom server invite links.
          (See our{' '}
          <a href="/discord/safety/platform-manipulation-policy-explainer">
            Platform Manipulation Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
    policyLink: '/discord/safety/platform-manipulation-policy-explainer',
  },
  '17': {
    number: '17',
    title: {
      cs: 'Nesdílejte nepravdivé nebo zavádějící informace (jinak známé jako dezinformace).',
      en: 'Do not share false or misleading information (otherwise known as misinformation).',
    },
    content: {
      cs: (
        <>
          Obsah, který je nepravdivý, zavádějící a může vést k významnému riziku fyzické nebo
          společenské újmy, nesmí být na Discordu sdílen. Obsah můžeme odstranit, pokud oprávněně
          věříme, že jeho šíření by mohlo vést k poškození fyzické infrastruktury, zranění
          ostatních, překážce účasti v občanských procesech nebo ohrožení veřejného zdraví. (Více
          informací najdete v našem{' '}
          <a href="https://discord.com/safety/misinformation-policy-explainer">
            Vysvětlení zásad dezinformací
          </a>
          .)
        </>
      ),
      en: (
        <>
          Content that is false, misleading, and could lead to significant risk of physical or
          societal harm may not be shared on Discord. We may remove content if we reasonably believe
          its spread could result in damage to physical infrastructure, injury to others,
          obstruction of participation in civic processes, or the endangerment of public health.
          (See our{' '}
          <a href="https://discord.com/safety/misinformation-policy-explainer">
            Misinformation Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
  },
  '18': {
    number: '18',
    title: {
      cs: 'Nereprezentujte svou identitu na Discordu klamavým nebo škodlivým způsobem.',
      en: 'Do not misrepresent your identity on Discord in a deceptive or harmful way.',
    },
    content: {
      cs: (
        <>
          To zahrnuje vytváření falešných profilů a pokusy vydávat se za jednotlivce, skupinu nebo
          organizaci. (Více informací najdete v našem{' '}
          <a href="/discord/safety/identity-authenticity-policy-explainer">
            Vysvětlení zásad identity a autenticity
          </a>
          .)
        </>
      ),
      en: (
        <>
          This includes creating fake profiles and attempts to impersonate an individual, group, or
          organization. (See our{' '}
          <a href="/discord/safety/identity-authenticity-policy-explainer">
            Identity and Authenticity Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
    policyLink: '/discord/safety/identity-authenticity-policy-explainer',
  },
  '19': {
    number: '19',
    title: {
      cs: 'Neobcházejte trvalé vynucovací akce na úrovni Discord.',
      en: 'Do not evade permanent Discord-level enforcement actions.',
    },
    content: {
      cs: (
        <>
          To zahrnuje vytváření nových nebo používání existujících účtů nebo komunit po odstranění
          za porušení našich pravidel platformy. (Více informací najdete v našem{' '}
          <a href="/discord/safety/identity-authenticity-policy-explainer">
            Vysvětlení zásad identity a autenticity
          </a>
          .)
        </>
      ),
      en: (
        <>
          This includes creating new or using existing accounts or communities after being removed
          for violating our platform rules. (See our{' '}
          <a href="/discord/safety/identity-authenticity-policy-explainer">
            Identity and Authenticity Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
    policyLink: '/discord/safety/identity-authenticity-policy-explainer',
  },
  '20': {
    number: '20',
    title: {
      cs: 'Nezapojujte se do činností, které by mohly poškodit nebo ohrozit bezpečnost účtu, sítě nebo systému.',
      en: 'Do not engage in activities that could damage or compromise the security of an account, network, or system.',
    },
    content: {
      cs: (
        <>
          To zahrnuje používání klamavých technik k oklamání ostatních, aby odhalili citlivé
          informace (phishing), používání škodlivého softwaru (malware) a zahlcení cíle provozem za
          účelem znepřístupnění zdroje (útoky odmítnutí služby). (Více informací najdete v našem{' '}
          <a href="/discord/safety/deceptive-practices-policy-explainer">
            Vysvětlení zásad klamavých praktik
          </a>
          .)
        </>
      ),
      en: (
        <>
          This includes using deceptive techniques to trick others into revealing sensitive
          information (phishing), using malicious software (malware), and flooding a target with
          traffic in order to make a resource unavailable (denial-of-service attacks). (See our{' '}
          <a href="/discord/safety/deceptive-practices-policy-explainer">
            Deceptive Practices Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
    policyLink: '/discord/safety/deceptive-practices-policy-explainer',
  },
  '21': {
    number: '21',
    title: {
      cs: 'Nepoužívejte ani se nepokoušejte používat Discord k propagaci, koordinaci nebo provádění finančních podvodů.',
      en: 'Do not use or attempt to use Discord to promote, coordinate, or execute financial scams.',
    },
    content: {
      cs: (
        <>
          Finanční podvod je jakýkoli úmyslně klamavý čin učiněný s úmyslem získat nezákonný,
          neetický nebo jinak nečestný zisk. (Více informací najdete v našem{' '}
          <a href="/discord/safety/deceptive-practices-policy-explainer">
            Vysvětlení zásad klamavých praktik
          </a>
          .)
        </>
      ),
      en: (
        <>
          A financial scam is any intentionally deceptive act taken with the intent to receive an
          illegal, unethical, or otherwise dishonest gain. (See our{' '}
          <a href="/discord/safety/deceptive-practices-policy-explainer">
            Deceptive Practices Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
    policyLink: '/discord/safety/deceptive-practices-policy-explainer',
  },
  '22': {
    number: '22',
    title: {
      cs: 'Nezapojujte se do činností, které podvodně generují zisk na úkor ostatních.',
      en: 'Do not engage in activities that fraudulently generate a profit at the expense of others.',
    },
    content: {
      cs: (
        <>
          To zahrnuje usnadňování, poskytování pokynů a účast na podvodech. Nepovolujeme
          koordinované úsilí o podvádění podniků, předražování cen, padělání, praní špinavých peněz,
          padělané zboží nebo nástroje, které usnadňují nezákonné chování. (Více informací najdete v
          našem{' '}
          <a href="/discord/safety/deceptive-practices-policy-explainer">
            Vysvětlení zásad klamavých praktik
          </a>
          .)
        </>
      ),
      en: (
        <>
          This includes facilitating, providing instructions for, and participating in fraud. We do
          not allow coordinated efforts to defraud businesses, price gouging, forgery, money
          laundering, counterfeit goods, or tools that facilitate illegal behavior. (See our{' '}
          <a href="/discord/safety/deceptive-practices-policy-explainer">
            Deceptive Practices Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
    policyLink: '/discord/safety/deceptive-practices-policy-explainer',
  },
  '23': {
    number: '23',
    title: {
      cs: 'Nepodávejte falešná, zavádějící nebo zneužívající hlášení nebo žádosti týmům podpory Discord.',
      en: "Do not submit false, misleading, or abusive reports or requests to Discord's support teams.",
    },
    content: {
      cs: (
        <>
          Hlášení nebo žádost bude považována za falešnou nebo zavádějící, pokud obsahuje
          prokazatelně nepravdivé informace předložené s úmyslem manipulovat zaměstnance k provedení
          požadované akce. (Více informací najdete v našem{' '}
          <a href="https://discord.com/safety/discord-support-abuse-policy-explainer">
            Vysvětlení zásad zneužívání podpory Discord
          </a>
          .)
        </>
      ),
      en: (
        <>
          A report or request will be considered false or misleading if it contains demonstrably
          untrue information submitted with the intent to manipulate staff into taking a desired
          action. (See our{' '}
          <a href="https://discord.com/safety/discord-support-abuse-policy-explainer">
            Discord Support Abuse Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
  },
  '24': {
    number: '24',
    title: {
      cs: 'Nesdílejte obsah, který porušuje duševní vlastnictví nebo jiná práva kohokoli.',
      en: "Do not share content that violates anyone's intellectual property or other rights.",
    },
    content: {
      cs: (
        <>
          To zahrnuje sdílení nebo prodej herních cheatů nebo hacků. (Více informací najdete v
          našich{' '}
          <a href="https://discord.com/safety/copyright-trademark-policy-explainer">
            Zásadách neoprávněného přístupu k autorským právům
          </a>
          .)
          <p>
            Pro více informací o tom, jak Discord zachází se stížnostmi na porušení autorských práv,
            se podívejte na naše{' '}
            <a href="https://support.discord.com/hc/en-us/articles/4410339349655-Discord-s-Copyright-IP-Policy">
              Zásady autorských práv a duševního vlastnictví
            </a>
            .
          </p>
        </>
      ),
      en: (
        <>
          This includes sharing or selling game cheats or hacks. (See our{' '}
          <a href="https://discord.com/safety/copyright-trademark-policy-explainer">
            Unauthorized Copyright Access Policy
          </a>{' '}
          for more.)
          <p>
            For more information on how Discord handles copyright complaints, please view our{' '}
            <a href="https://support.discord.com/hc/en-us/articles/4410339349655-Discord-s-Copyright-IP-Policy">
              Copyright & Intellectual Property Policy
            </a>
            .
          </p>
        </>
      ),
    },
  },
  '25': {
    number: '25',
    title: {
      cs: 'Neorganizujte, nepropagujte ani se nezapojujte do prodeje nebo usnadňování prodeje regulovaného nebo potenciálně nebezpečného zboží.',
      en: 'Do not organize, promote, or engage in the selling or facilitation of sales of regulated or potentially dangerous goods.',
    },
    content: {
      cs: (
        <>
          Regulované zboží má zákony, které omezují nákup, prodej, obchod nebo vlastnictví zboží.
          Nebezpečné zboží má přiměřený potenciál způsobit skutečnou fyzickou újmu jednotlivcům.
          (Více informací najdete v našem{' '}
          <a href="https://discord.com/safety/dangerous-regulated-goods-policy-explainer">
            Vysvětlení zásad nebezpečného a regulovaného zboží
          </a>
          .)
        </>
      ),
      en: (
        <>
          Regulated goods have laws in place that restrict the purchase, sale, trade, or ownership
          of the goods. Dangerous goods have a reasonable potential to cause real-world, physical
          harm to individuals. (See our{' '}
          <a href="https://discord.com/safety/dangerous-regulated-goods-policy-explainer">
            Dangerous and Regulated Goods Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
  },
  '26': {
    number: '26',
    title: {
      cs: 'Nekoordinujte ani se neúčastněte nelegálního hazardu.',
      en: 'Do not coordinate or participate in illegal gambling.',
    },
    content: {
      cs: (
        <>
          Uživatelé jsou zodpovědní za dodržování platných zákonů a předpisů o hazardních hrách.
          (Více informací najdete v našem{' '}
          <a href="https://discord.com/safety/gambling-policy-explainer">
            Vysvětlení zásad hazardních her
          </a>
          .)
        </>
      ),
      en: (
        <>
          Users are responsible for complying with applicable gambling laws and regulation. (See our{' '}
          <a href="https://discord.com/safety/gambling-policy-explainer">
            Gambling Policy Explainer
          </a>{' '}
          for more.)
        </>
      ),
    },
  },
  '27': {
    number: '27',
    title: {
      cs: 'Neorganizujte, nepropagujte ani se nezapojujte do nezákonné činnosti ohrožující důstojnost, bezpečnost nebo blaho druhých,',
      en: 'Do not organize, promote, or engage in illegal activity that harms others’ dignity, safety, or well-being,',
    },
    content: {
      cs: (
        <>
          jako je obchodování s lidmi nebo sexuální nabízení. (Více informací najdete v našich
          vysvětleních zásad{' '}
          <a href="https://discord.com/safety/human-trafficking-policy-explainer">
            Obchodování s lidmi
          </a>{' '}
          a <a href="/discord/safety/sexual-solicitation-policy-explainer">Sexuální nabízení</a>.)
        </>
      ),
      en: (
        <>
          such as human trafficking or sexual solicitation. (See our{' '}
          <a href="https://discord.com/safety/human-trafficking-policy-explainer">
            Human Trafficking
          </a>{' '}
          and{' '}
          <a href="/discord/safety/sexual-solicitation-policy-explainer">
            Sexual Solicitation Policy Explainers
          </a>{' '}
          for more.)
        </>
      ),
    },
    policyLink: '/discord/safety/sexual-solicitation-policy-explainer',
  },
}

// Helper function to get guideline by number
export function getGuideline(number: string): GuidelineData | undefined {
  return guidelines[number]
}

// Helper function to get guideline content only (for backwards compatibility)
export function getGuidelineContent(
  number: string,
  language: 'cs' | 'en',
): React.ReactNode | undefined {
  const guideline = guidelines[number]
  if (!guideline) return undefined

  return (
    <>
      <strong>
        {guideline.number}. {guideline.title[language]}
      </strong>{' '}
      {guideline.content[language]}
    </>
  )
}
